import { createContext } from 'react';

import { AUTO_LANGUAGE, STORAGE_KEYS, isLanguageCode } from '@config';

const defaultContextValue = {} as UserContextType;

type UserState = {
  hasFetched: boolean;
  loggedIn: boolean;
  user: User | null;
  /**
   * What the user picked in their profile, NOT the locale in effect. `'auto'`
   * means "follow the active organization"; `useLanguage` resolves the two.
   */
  languagePreference: LanguagePreference;
};

type UserAction = {
  type: string;
  user?: User | null;
  language?: LanguagePreference;
};

type UserContextType = {
  state: UserState;
  dispatch: React.Dispatch<UserAction>;
};

export const UserContext = createContext<UserContextType>(defaultContextValue);

/**
 * The pin a previous session left behind, ignoring anything that is not a
 * locale we ship — a stale or hand-edited value must read as "no pin" rather
 * than send lingui after a catalogue that does not exist.
 */
export const readStoredLanguagePreference = (): LanguagePreference => {
  const stored = localStorage.getItem(STORAGE_KEYS.LANGUAGE_PREFERENCE);
  return isLanguageCode(stored) ? stored : AUTO_LANGUAGE;
};

export const initialUserState: UserState = {
  hasFetched: false,
  loggedIn: false,
  user: null,
  languagePreference: readStoredLanguagePreference(),
};

export const userReducer = (state: UserState, action: UserAction): UserState => {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        hasFetched: true,
        loggedIn: !!action.user,
        user: action.user as User | null,
      };
    // case 'LOGOUT':
    //   return {
    //     ...state,
    //     loggedIn: false,
    //     user: null,
    //   };
    case 'SET_LANGUAGE': {
      const preference = action.language as LanguagePreference;
      // "Automatic" is the absence of a pin, so it is removed rather than
      // stored — a future locale rename then cannot leave a dead value behind.
      if (preference === AUTO_LANGUAGE) {
        localStorage.removeItem(STORAGE_KEYS.LANGUAGE_PREFERENCE);
      } else {
        localStorage.setItem(STORAGE_KEYS.LANGUAGE_PREFERENCE, preference);
      }
      return {
        ...state,
        languagePreference: preference,
      };
    }
    default:
      return state;
  }
};

export const setUser = (user: User | null) => ({
  type: 'SET_USER',
  user,
});

// export const logoutUser = () => ({
//   type: 'LOGOUT',
// });

export const setLanguage = (language: LanguagePreference) => ({
  type: 'SET_LANGUAGE',
  language,
});
